import { DOCUMENT } from '@angular/common';
import { ChangeDetectorRef, Component, Inject, Input, OnInit, ViewChild } from '@angular/core';
import { AlertService } from '@app/@core/services/alert.service';
import { FormioComponent } from '@formio/angular';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { PageBuilderAddService } from '@app/pages/page-designer/pagebuilderAddservice';
import { catchError } from 'rxjs';
import { AuthService, FormioService } from '@pics-core/dynamic';

@Component({
  standalone: false,
  selector: 'app-form-modal-component',
  templateUrl: './form-modal.component.html',
  styleUrls: ['./form-modal.component.scss']
})
export class FormModalComponent implements OnInit {
  @Input()
  title: string;

 @Input() action: string = 'Save';

  @Input()
  submittedData: any;

  @Input()
  formJson: any;

  @Input()
  formId: string;

  @Input()
  customData: any;

  @Input()
  viewForm: boolean;

  @Input()
  id: number | null = null;

  @Input()
  gridItems: any[] = [];

  @ViewChild('formIO', { static: false })
  formIO: FormioComponent;

  @Input()
  gridId: string;

  @Input()
  assessmentType: boolean;

  renderOptions: any;

  formOptions = {
    noAlerts: true,
    disableAlerts: true
  };

  queryCondition: any = {};

  // Properties for reset functionality
  originalFormData: any = {};
  hasFormChanged: boolean = false;
  isResetButtonDisabled: boolean = true;
  constructor(
    private readonly activeModal: NgbActiveModal,
    private readonly alertService: AlertService,
    private readonly pageBuilderService: PageBuilderAddService,
    @Inject(DOCUMENT) private readonly document: Document,
    private formIoService: FormioService,
    private readonly cdr: ChangeDetectorRef,
    private readonly authService: AuthService,
  ) {
    // For ES LINT
  }

  ngOnInit() {
     this.authService.clearFormAttachments();
    const _component = this;
    this.renderOptions = {
      hooks: {
        beforeSubmit: function (submission, callback) {
          if (!this.checkValidity()) {
            _component.alertService?.error('Please Fill Required Fields');
            setTimeout(() => {
              (
                _component.document.querySelector(
                  'input.is-invalid, select.is-invalid, .selection.is-invalid'
                ) as HTMLElement
              )?.focus();
            }, 500);
          }
          callback(null, submission);
        }
      }
    };

    // Store original form data when the component initializes
    this.storeOriginalFormData();
  }

  closePopup() {
    this.activeModal.dismiss(false);
     this.pageBuilderService.clearCacheFileByKey(`f-${this.formId}-${this.id}`);
  }

  ngOnDestroy(){
    if (this.formIO?.formio) {
      this.formIO.formio.off('customEvent');
    }
   this.pageBuilderService.clearCacheFileByKey(`f-${this.formId}-${this.id}`);
    this.pageBuilderService.clearCacheByKey('verifiedAddress');
  }
  ngAfterViewInit(): void {
    if (this.formIO && !this.viewForm) {
      this.formIO.formio.on('change', (event) => {
        const focusedElement = document.activeElement;
        const hiddenAncestor = focusedElement?.closest('[aria-hidden="true"]');
        if (hiddenAncestor) {
          hiddenAncestor.setAttribute('aria-hidden', 'false');
          console.warn('Removed aria-hidden from:', hiddenAncestor);
        }
        event.data = {...event.data,
        pageId: this.formId,
        id:this.id        }
        this.formIoService.setPopupFormData(event);

        // Track form changes for reset button state
        this.checkFormChanges(event.data);

        this.cdr.detectChanges();
      });

      // Store original data after form is initialized
      setTimeout(() => {
        this.storeOriginalFormData();
      }, 100);
    }
  }
  onSave() {
    this.formIO.formio.submit();
    // this.activeModal.dismiss();
  }

  onSubmit($event) {
    const formioInstance = this.formIO?.formio;
    const formData = $event?.data || this.submittedData?.data;
    if (formioInstance) {
      const isValid = formioInstance.checkValidity(formData, true);
      if (!isValid) {
        this.alertService?.error('Please fill required fields');
        setTimeout(() => {
          const invalidElement = document.querySelector(
            'input.is-invalid, select.is-invalid, .selection.is-invalid, textarea.is-invalid'
          ) as HTMLElement;
          if (invalidElement) {
            invalidElement.focus();
          }
        }, 500);
        return;
      }
    }

    // Always check for duplicates, but exclude current record when editing
    const mergedData = { ...formData, ...this.customData };
    if (this.isDuplicateEntry(mergedData, this.id)) {
      this.alertService?.error('Duplicate entry detected. This record already exists.');
      return;
    }

    const allAttachments = Object.entries(formData)
      .filter(([, value]: [string, any]) => Array.isArray(value?.attachmentdetails) && value.attachmentdetails.length > 0)
      .reduce((acc: any[], [field, value]: [string, any]) => {
        const files = value.attachmentdetails.map((file: any) => {
          if (file?.path?.split('/')[5] !== field) return null;
           return {
          name: file.name,
          path: file.path.startsWith('http') ? file.path : `https://wy-base-doc.s3.amazonaws.com/${file.path}`,
          attachmenttype: file.attachmenttype || file.type || 'application/octet-stream',
          field,
          formid: value.formid,
          pageId: value.pageId,
          id: value.id,
          type: value.type
           };
        }).filter(Boolean);
        return acc.concat(files);
      }, []);
    const payload = {
      pageid: this.formId,
      response: $event.data
    };

    if (this.customData) {
      if (this.customData?.tbl_status && payload.response?.comments) {
        this.customData.tbl_status.comments = payload.response.comments;
      }
      payload.response = {
        ...payload.response,
        ...this.customData
      };
    }
    let subscription;
    if (this.id) {
      subscription = this.assessmentType ? this.pageBuilderService.updateNestedForm(payload, this.id) : this.pageBuilderService.editForm(payload, this.id);
    } else {
      subscription = this.assessmentType ? this.pageBuilderService.submitNestedForm(payload) : this.pageBuilderService.submitForm(payload);
    }
    subscription
      .pipe(
        catchError(err => {
          this.alertService.error('Something went wrong! Please try again later');
          console.log(err);
          throw err;
        })
      )
      .subscribe((data: any) => {
        const resultId = data?.data?.id ?? this.id;
        if(this.assessmentType){
          this.alertService.success('Assessment has been initiated successfully!');
        } else {
          this.alertService.success(`${this.id ? 'Updated' : 'Added'} Successfully.`);
        }
        this.activeModal.dismiss({
          submittedData: $event.data,
          serverResponse: this.assessmentType ? data : true
        })
        if (allAttachments.length && resultId) {
          const grouped = this.groupBy(allAttachments, 'field');
          Object.entries(grouped).forEach(async ([field, attachments]: [string, any[]]) => {
            const attachmentPayload = {
              responseid: Number(resultId),
              type: attachments[0].type,
              formid: this.formId,
              pageId: this.formId,
              field,
              attachmentdetails: attachments.map(file => ({
                name: file.name,
                path: file.path,
                attachmenttype: file.attachmenttype,
                field
              }))
            };
            await this.addAttachment(attachmentPayload);
            this.pageBuilderService.clearCacheFileByKey(`f-${this.formId}-${resultId}`);
          });
        }
      });
  }

  resetForm() {
    if (this.formIO && this.formIO.formio) {
      // Reset to original values instead of clearing
      this.formIO.formio.submission = { data: this.originalFormData };
      this.formIO.formio.setValue(this.originalFormData);

      // Reset form change tracking
      this.hasFormChanged = false;
      this.isResetButtonDisabled = true;
      this.cdr.detectChanges();
    }
  }

  private storeOriginalFormData(): void {
    if (this.submittedData?.data) {
      // Deep clone the submitted data to avoid reference issues
      this.originalFormData = JSON.parse(JSON.stringify(this.submittedData.data));
    } else if (this.formIO?.formio?.data) {
      // If no submitted data, use current form data as original
      this.originalFormData = JSON.parse(JSON.stringify(this.formIO.formio.data));
    } else {
      // If no data available, initialize as empty object
      this.originalFormData = {};
    }
  }

  private checkFormChanges(currentData: any): void {
    if (!this.originalFormData) {
      return;
    }

    // Compare current data with original data
    const hasChanges = !this.isEqual(currentData, this.originalFormData);

    if (hasChanges !== this.hasFormChanged) {
      this.hasFormChanged = hasChanges;
      this.isResetButtonDisabled = !hasChanges;
    }
  }

  private isEqual(obj1: any, obj2: any): boolean {
    if (obj1 === obj2) return true;
    if (obj1 == null || obj2 == null) return false;
    if (typeof obj1 !== typeof obj2) return false;

    if (typeof obj1 === 'object') {
      if (Array.isArray(obj1) !== Array.isArray(obj2)) return false;

      if (Array.isArray(obj1)) {
        if (obj1.length !== obj2.length) return false;
        for (let i = 0; i < obj1.length; i++) {
          if (!this.isEqual(obj1[i], obj2[i])) return false;
        }
        return true;
      }

      const keys1 = Object.keys(obj1);
      const keys2 = Object.keys(obj2);
      if (keys1.length !== keys2.length) return false;

      for (const key of keys1) {
        if (!keys2.includes(key)) return false;
        if (!this.isEqual(obj1[key], obj2[key])) return false;
      }
      return true;
    }

    return obj1 === obj2;
  }

  private groupBy(array: any[], key: string): { [key: string]: any[] } {
    return array.reduce((acc, item) => {
      const group = item[key];
      if (!acc[group]) acc[group] = [];
      acc[group].push(item);
      return acc;
    }, {} as { [key: string]: any[] });
  }

  async addAttachment(info: { responseid: number, attachmentdetails: any[] }): Promise<void> {
    await this.pageBuilderService.createFormResponseAttachment(info).subscribe(() => {
  //  this.authService.setSharedMessage({ ...info, attachmentdetails: [] });
      });
  }

  private isDuplicateEntry(formData: any, currentRecordId: number | null = null): boolean {
    if (!this.gridItems || this.gridItems.length === 0) {
      return false;
    }

    const excludeFields = ['id', 'primary_id', 'primary_object', 'application_id', 'person_id',
                           'pageId', 'formid', 'type', 'comments', 'textField', 'value', 'endorsement',
                           'license_kind_endorsement_id', 'created_by', 'created_date',
                           'updated_by', 'updated_date', 'is_active', 'is_read_only', 'is_new'];

    const formDataFields = Object.keys(formData).filter(key =>
      !excludeFields.includes(key) &&
      !key.startsWith('tbl_') &&
      !key.endsWith('_id') &&
      !key.includes('created') &&
      !key.includes('updated') &&
      typeof formData[key] !== 'object' &&
      formData[key] !== null &&
      formData[key] !== undefined &&
      formData[key] !== ''
    );

    if (formDataFields.length === 0) {
      return false;
    }

     return this.gridItems.some((gridItem, index) => {
      // Skip comparison with the current record being edited
      if (currentRecordId !== null) {
        const gridItemId = gridItem.primary_id || gridItem.id;
        if (gridItemId == currentRecordId) {
          console.log(`Skipping grid item [${index}] - it's the record being edited`);
          return false;
        }
      }

      let matchCount = 0;
      let fieldsChecked = 0;

      for (const field of formDataFields) {
        const formValue = formData[field];
        let gridValue = undefined;
        let fieldExists = false;

        if (gridItem.hasOwnProperty(field)) {
          gridValue = gridItem[field];
          fieldExists = true;
        } else {
          for (const key in gridItem) {
            if (typeof gridItem[key] === 'object' && gridItem[key] !== null && !Array.isArray(gridItem[key])) {
              if (gridItem[key].hasOwnProperty(field)) {
                gridValue = gridItem[key][field];
                fieldExists = true;
                break;
              }
            }
          }
        }

       if (!fieldExists) {
          console.log(`  Field '${field}' not found in grid item - skipping this field`);
          continue;
        }

        fieldsChecked++;

        const normalizedFormValue = this.normalizeValue(formValue);
        const normalizedGridValue = this.normalizeValue(gridValue);
        const isMatch = normalizedFormValue === normalizedGridValue && normalizedFormValue !== '';

        if (isMatch) {
          matchCount++;
        } else {
         return false;
        }
      }

      // Consider it a duplicate if we checked at least one field and all checked fields matched
      const result = fieldsChecked > 0 && matchCount === fieldsChecked;
      console.log(`Grid item [${index}]: ${matchCount}/${fieldsChecked} fields matched (${formDataFields.length} total form fields), isDuplicate=${result}`);
      return result;
    });

  }


  private normalizeValue(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }

    // Handle Date objects or date strings
    if (value instanceof Date || this.isDateString(value)) {
      return this.normalizeDateValue(value);
    }

    // Handle numbers (including decimal numbers)
    if (typeof value === 'number' || !isNaN(parseFloat(value))) {
      return parseFloat(value).toString();
    }

    if (typeof value === 'object') {
      return JSON.stringify(value);
    }

    return String(value).trim().toLowerCase();
  }

  private isDateString(value: any): boolean {
    if (typeof value !== 'string') {
      return false;
    }

    // Check for common date formats: YYYY-MM-DD, YYYY-MM-DDTHH:mm:ss, etc.
    const datePattern = /^\d{4}-\d{2}-\d{2}/;
    return datePattern.test(value);
  }

  private normalizeDateValue(value: any): string {
    try {
      let dateObj: Date;

      if (value instanceof Date) {
        dateObj = value;
      } else {
        dateObj = new Date(value);
      }

      // Check if valid date
      if (isNaN(dateObj.getTime())) {
        return String(value).trim().toLowerCase();
      }

      // Return in YYYY-MM-DD format
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');

      return `${year}-${month}-${day}`;
    } catch (e) {
      return String(value).trim().toLowerCase();
    }
  }
}
